/**
 * alatunniste.js — the footer reveal guard.
 *
 * WHY THIS FILE EXISTS AT ALL, given that §6.1 item 8 is native-first and the
 * nine front-page sections contain no JavaScript: a `position: fixed;
 * bottom: 0` footer that is TALLER than the viewport pins its own top
 * off-screen at every scroll position. It is anchored to the bottom of the
 * viewport, not to the bottom of the document, so no amount of scrolling
 * recovers the part that overhangs. Measured in the preview at 390x844 with a
 * 1719px footer: the top sat at a constant -875px, and the sign-off line, the
 * CTA, the wordmark, the brand paragraph and two whole columns could not be
 * reached on a phone at all.
 *
 * So the reveal is an enhancement and the content is not. This file decides
 * which of the two states the page is in, and nothing else. It is registered as
 * departure 11 in docs/open-work.md.
 *
 * The constraints it is held to, all of them binding (the same list the blog's
 * one script carries in its own header):
 *
 *   - It does nothing if its element is absent.
 *   - Everything works with it blocked: the base state is the ordinary in-flow
 *     footer, fully visible, and every CSS rule the reveal needs is behind the
 *     body class this file adds.
 *   - Reduced motion turns it off, and keeps it off.
 *   - No library, no third-party request, no build step.
 *   - No scroll listener. Horizon scrolls .page-wrapper rather than the
 *     document above 990px, so `window.scrollY` is permanently 0 on desktop and
 *     a scroll handler on window would never fire. Nothing here needs one: the
 *     answer depends on the footer's height against the viewport's, and both
 *     change only on resize.
 */

(() => {
  const REVEAL_CLASS = 'alatunniste-paljastus';
  const HEIGHT_PROPERTY = '--alatunniste-korkeus';

  /* How much the footer may overhang the viewport before the reveal is turned
     off. Not zero: at exactly viewport height the top row is flush with the top
     edge, which is fine, and a couple of dozen pixels of the photo spacer
     scrolling past is not content. 72px is the preview's tested value. */
  const OVERHANG_TOLERANCE = 72;

  /* ⚠️ MEASURE THE LAYOUT'S <footer>, NOT .alatunniste (changed 2026-09-23).
     The reveal fixes `.page-wrapper > footer` — the whole footer GROUP — and
     reserves space for it below the page. That was the same height as
     .alatunniste while the group held one section. The contact form
     (sections/alatunniste-yhteydenotto.liquid) is now a second section in the
     group, above it, so measuring .alatunniste alone reserved too little and
     the page would have scrolled up over the form. */
  const footer = document.querySelector('.alatunniste')?.closest('footer') ?? document.querySelector('.alatunniste');
  if (!footer) return;

  const reduceMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)');

  const disable = () => {
    document.body.classList.remove(REVEAL_CLASS);
    document.documentElement.style.removeProperty(HEIGHT_PROPERTY);
  };

  const sync = () => {
    if (reduceMotion.matches) {
      disable();
      return;
    }

    const height = footer.offsetHeight;
    if (height - globalThis.innerHeight > OVERHANG_TOLERANCE) {
      disable();
      return;
    }

    /* The property goes on <html> rather than on <body>: custom properties
       inherit downward only, and the rules that read it sit on .content-for-
       layout, which is a descendant of both — but open-work N25 records what
       happens when a property meant for the whole document is declared on the
       lower of the two. */
    document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
    document.body.classList.add(REVEAL_CLASS);
  };

  sync();

  /* The footer's own height changes when the photograph finishes loading, when
     a link column rewraps, and on every edit in the theme editor. */
  if (globalThis.ResizeObserver) new globalThis.ResizeObserver(sync).observe(footer);
  globalThis.addEventListener('resize', sync);
  reduceMotion.addEventListener('change', sync);
})();
